import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { cn } from '@/lib/utils';
import type {
  CSVImportRowResult,
  CSVImportWorkflowProgress,
} from '@/services/api/providerDirectoryCSVImportService';
import {
  useGetCSVImportStatusQuery,
  useUploadCSVImportMutation,
} from '@/services/api/providerDirectoryCSVImportService';

const TEMPLATE_URL = '/api/provider-directory/v2/csv-import/template/';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Progress bar with percentage label — v2 styled. */
const ProgressBar = ({
  progress,
}: {
  progress: CSVImportWorkflowProgress;
}) => {
  const total = progress.total_rows || 1;
  const pct = Math.min(
    100,
    Math.round((progress.rows_processed / total) * 100),
  );
  const isActive =
    progress.status === 'PROCESSING' ||
    progress.status === 'PARSING' ||
    progress.status === 'PENDING';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-xs font-medium">
        <span className="text-zinc-500">
          {progress.rows_processed} / {progress.total_rows} rows
        </span>
        <span
          className={cn(
            isActive ? 'text-primary' : 'text-success',
            progress.status === 'FAILED' && 'text-error',
          )}
        >
          {pct}%
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            isActive && 'bg-primary',
            progress.status === 'COMPLETED' && 'bg-success',
            progress.status === 'FAILED' && 'bg-error',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs font-medium text-zinc-500">
        {progress.status === 'PARSING' && 'Parsing CSV...'}
        {progress.status === 'PROCESSING' &&
          `Processing rows (${progress.matched} matched, ${progress.skipped} skipped, ${progress.errored} errored)`}
        {progress.status === 'COMPLETED' && 'Import complete'}
        {progress.status === 'FAILED' &&
          (progress.error_message ?? 'Import failed')}
      </div>
    </div>
  );
};

/** Summary badges for completed results — custom pills. */
const ResultSummary = ({
  progress,
  dryRun,
}: {
  progress: CSVImportWorkflowProgress;
  dryRun: boolean;
}) => (
  <div className="flex flex-wrap gap-1.5">
    {dryRun && (
      <span className="inline-flex items-center rounded-full bg-[#FFF3ED] px-2.5 py-1 text-[11px] font-semibold text-warning">
        Dry Run
      </span>
    )}
    <span className="inline-flex items-center rounded-full bg-[#EDFFF0] px-2.5 py-1 text-[11px] font-semibold text-success">
      {progress.matched} matched
    </span>
    <span className="inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] font-semibold text-zinc-600">
      {progress.skipped} skipped
    </span>
    {progress.errored > 0 && (
      <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-error">
        {progress.errored} errored
      </span>
    )}
    {!dryRun && (
      <>
        <span className="inline-flex items-center rounded-full bg-[#F1EFFF] px-2.5 py-1 text-[11px] font-semibold text-primary">
          {progress.contact_points_created} created
        </span>
        <span className="inline-flex items-center rounded-full bg-[#F1EFFF] px-2.5 py-1 text-[11px] font-semibold text-primary">
          {progress.contact_points_updated} updated
        </span>
      </>
    )}
  </div>
);

/** Expandable row-level results table. */
const RowResultsTable = ({
  rows,
}: {
  rows: CSVImportRowResult[];
}) => {
  const [expanded, setExpanded] = useState(false);
  const display = expanded ? rows : rows.slice(0, 10);

  const statusColors: Record<string, string> = {
    matched: 'text-success font-semibold',
    skipped: 'text-zinc-500 font-medium',
    errored: 'text-error font-semibold',
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="max-h-64 overflow-auto rounded-lg border border-zinc-100">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#F9F9FB]">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-zinc-500">
                #
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-zinc-500">
                Provider
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-zinc-500">
                Status
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-zinc-500">
                Detail
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {display.map((row) => (
              <tr key={row.row_index}>
                <td className="px-3 py-2 text-zinc-500">
                  {row.row_index + 1}
                </td>
                <td className="max-w-[180px] truncate px-3 py-2 font-semibold text-info-dark">
                  {row.provider_name}
                </td>
                <td className="px-3 py-2">
                  <span className={statusColors[row.status] ?? 'text-zinc-500'}>
                    {row.status}
                  </span>
                </td>
                <td className="max-w-[220px] truncate px-3 py-2 font-medium text-zinc-500">
                  {row.provider_service_name ??
                    row.skip_reason ??
                    row.error ??
                    ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 10 && (
        <button
          type="button"
          className="text-xs font-semibold text-primary hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `Show all ${rows.length} rows`}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const CSVImportSection = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [markVerified, setMarkVerified] = useState(false);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [uploadCSV, { isLoading: isUploading }] =
    useUploadCSVImportMutation();

  // Track whether the workflow has finished so we can stop polling
  const [pollingStopped, setPollingStopped] = useState(false);

  // Poll workflow status when we have a workflowId; stop once done
  const { data: statusData, isLoading: isStatusLoading } =
    useGetCSVImportStatusQuery(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by skip: !workflowId
      { workflowId: workflowId! },
      {
        skip: !workflowId,
        pollingInterval: workflowId && !pollingStopped ? 2000 : 0,
      },
    );

  const isRunning =
    statusData?.progress.status === 'PROCESSING' ||
    statusData?.progress.status === 'PARSING' ||
    statusData?.progress.status === 'PENDING';

  const isDone =
    statusData?.progress.status === 'COMPLETED' ||
    statusData?.progress.status === 'FAILED';

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setSelectedFile(file);
      setUploadError(null);
    },
    [],
  );

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setUploadError(null);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('dry_run', String(dryRun));
    formData.append('mark_verified', String(markVerified));

    try {
      const result = await uploadCSV(formData).unwrap();
      setWorkflowId(result.workflow_id);
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ??
        'Upload failed. Please try again.';
      setUploadError(msg);
    }
  }, [selectedFile, dryRun, markVerified, uploadCSV]);

  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setWorkflowId(null);
    setUploadError(null);
    setDryRun(true);
    setMarkVerified(false);
    setPollingStopped(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Only fully reset if not in the middle of processing
    if (!isRunning) {
      handleReset();
    }
  }, [isRunning, handleReset]);

  // Stop polling once workflow finishes
  useEffect(() => {
    if (isDone) {
      setPollingStopped(true);
    }
  }, [isDone]);

  return (
    <>
      {/* Trigger button — styled to match v2 action buttons */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3.5 py-1.5 text-[13px] font-semibold text-zinc-500 hover:bg-background-gray transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <Upload size={14} />
        CSV import
      </button>

      <Dialog open={isOpen} onClose={handleClose} size="2xl">
        <DialogTitle>
          <span className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F1EFFF]">
              <Upload size={16} className="text-primary" />
            </span>
            CSV Contact Import
          </span>
        </DialogTitle>
        <DialogDescription>
          Bulk import provider contact information from a CSV file. Rows are
          matched against the directory using the dedup resolver.
        </DialogDescription>

        <DialogBody>
          {/* --- Step 1: File selection (before upload) --- */}
          {!workflowId && (
            <div className="flex flex-col gap-4">
              {/* Template download */}
              <div className="flex items-center gap-3 rounded-xl border border-zinc-100 bg-[#F9F9FB] p-3.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EDFFF0]">
                  <FileSpreadsheet size={16} className="text-success" />
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                  <p className="text-[13px] font-semibold text-info-dark">
                    CSV Template
                  </p>
                  <p className="text-[11px] font-medium text-zinc-500">
                    provider_name · address · fax · phone · email · portal_url ·
                    source
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Use fetch with credentials so the auth cookie is sent
                    fetch(
                      `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${TEMPLATE_URL}`,
                      { credentials: 'include' },
                    )
                      .then((res) => {
                        if (!res.ok)
                          throw new Error(`Download failed: ${res.status}`);
                        return res.blob();
                      })
                      .then((blob) => {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'provider_contact_import_template.csv';
                        a.click();
                        URL.revokeObjectURL(url);
                      })
                      .catch(() => {
                        // Silently fail — template download is non-critical
                      });
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-background-gray focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Download size={12} />
                  Download
                </button>
              </div>

              {/* File upload zone */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-7 transition-colors',
                  'hover:border-primary/40 hover:bg-[#F9F8FF]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  selectedFile
                    ? 'border-primary/30 bg-[#F9F8FF]'
                    : 'border-zinc-200 bg-[#FAFAFA]',
                )}
              >
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg',
                    selectedFile ? 'bg-[#EDFFF0]' : 'bg-[#F1EFFF]',
                  )}
                >
                  {selectedFile ? (
                    <CheckCircle2 size={18} className="text-success" />
                  ) : (
                    <Upload size={18} className="text-primary" />
                  )}
                </div>
                {selectedFile ? (
                  <>
                    <span className="text-[13px] font-semibold text-info-dark">
                      {selectedFile.name}
                    </span>
                    <span className="text-[11px] font-medium text-zinc-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB · Click to
                      change
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-[13px] font-semibold text-info-dark">
                      Click to browse for your CSV file
                    </span>
                    <span className="text-[11px] font-medium text-zinc-500">
                      Accepts .csv files
                    </span>
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="hidden"
              />

              {/* Options */}
              <div className="flex flex-wrap gap-5">
                <label
                  htmlFor="csv-import-dry-run"
                  className="flex cursor-pointer items-center gap-2 text-[13px]"
                >
                  <input
                    id="csv-import-dry-run"
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    className="rounded border-zinc-300 text-primary focus:ring-primary"
                  />
                  <span className="font-semibold text-info-dark">Dry run</span>
                  <span className="text-[11px] font-medium text-zinc-500">
                    (preview only)
                  </span>
                </label>
                <label
                  htmlFor="csv-import-mark-verified"
                  className="flex cursor-pointer items-center gap-2 text-[13px]"
                >
                  <input
                    id="csv-import-mark-verified"
                    type="checkbox"
                    checked={markVerified}
                    onChange={(e) => setMarkVerified(e.target.checked)}
                    disabled={dryRun}
                    className="rounded border-zinc-300 text-primary focus:ring-primary disabled:opacity-50"
                  />
                  <span
                    className={cn(
                      'font-semibold text-info-dark',
                      dryRun && 'opacity-45',
                    )}
                  >
                    Mark as verified
                  </span>
                </label>
              </div>

              {/* Upload error */}
              {uploadError && (
                <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 p-3">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-error" />
                  <p className="text-sm font-medium text-error">
                    {uploadError}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* --- Step 2: Progress (during workflow) --- */}
          {workflowId && statusData && (
            <div className="flex flex-col gap-4">
              {/* Status icon + label */}
              <div className="flex items-center gap-2.5">
                {isRunning && (
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F1EFFF]">
                    <Loader2 size={16} className="animate-spin text-primary" />
                  </div>
                )}
                {statusData.progress.status === 'COMPLETED' && (
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#EDFFF0]">
                    <CheckCircle2 size={16} className="text-success" />
                  </div>
                )}
                {statusData.progress.status === 'FAILED' && (
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50">
                    <XCircle size={16} className="text-error" />
                  </div>
                )}
                <span className="text-sm font-semibold text-info-dark">
                  {isRunning && 'Import in progress...'}
                  {statusData.progress.status === 'COMPLETED' &&
                    'Import Complete'}
                  {statusData.progress.status === 'FAILED' && 'Import Failed'}
                </span>
                {dryRun && (
                  <span className="inline-flex items-center rounded-full bg-[#FFF3ED] px-2.5 py-0.5 text-[11px] font-semibold text-warning">
                    Dry Run
                  </span>
                )}
              </div>

              {/* Progress bar */}
              <ProgressBar progress={statusData.progress} />

              {/* Summary badges */}
              {isDone && (
                <ResultSummary
                  progress={statusData.progress}
                  dryRun={dryRun}
                />
              )}

              {/* Row-level results */}
              {statusData.result?.row_results &&
                statusData.result.row_results.length > 0 && (
                  <RowResultsTable rows={statusData.result.row_results} />
                )}

              {/* Error message */}
              {statusData.progress.status === 'FAILED' &&
                statusData.progress.error_message && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 p-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-error" />
                    <p className="text-sm font-medium text-error">
                      {statusData.progress.error_message}
                    </p>
                  </div>
                )}
            </div>
          )}

          {/* Loading state while first status call loads */}
          {workflowId && !statusData && isStatusLoading && (
            <div className="flex items-center gap-2 py-4">
              <Loader2 size={16} className="animate-spin text-primary" />
              <span className="text-sm font-medium text-zinc-500">
                Starting workflow...
              </span>
            </div>
          )}
        </DialogBody>

        <DialogActions>
          {/* Before upload */}
          {!workflowId && (
            <>
              <Button color="zinc" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                color="dark/white"
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={14} />
                    {dryRun ? 'Preview Import' : 'Start Import'}
                  </>
                )}
              </Button>
            </>
          )}

          {/* During / after workflow */}
          {workflowId && (
            <>
              {isDone && (
                <Button color="zinc" onClick={handleReset}>
                  Import Another
                </Button>
              )}
              <Button
                color={isDone ? 'dark/white' : 'zinc'}
                onClick={handleClose}
              >
                {isDone ? 'Done' : 'Close'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
